import SubMenu from 'common/navigation/components/sub-menu';
import MainMenu from 'common/navigation/containers/main-menu-container';
import Wrapper from 'common/navigation/wrapper';
import CreateOrders from 'order/create/containers/create-orders-container';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import React from 'react';
import { isToggleEnabled } from 'react-awesome-feature-toggles/dist/feature-toggles';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { withRouter } from 'utils/withRouter';
import { Div, InfoTooltip } from 'ui';
import styled from 'styled-components';
import usePermissionCheck from 'utils/hooks/usePermissionCheck';
import moment from 'utils/cops-moment';
import { fetchConnectionStatus } from '../equipment/info/containers/info-actions';
import { ticketObjectRoutes } from '../common/navigation/routes/cops-routes';
import TicketWorklistContainer from '../tickets/containers/list/ticket-worklist-container';
import { referenceTypes } from '../utils/app-configs';
import Dashboard from './components/dashboard';
import Timeline from './components/timeline';
import Info from './info/containers/info-container';

const WrapperState = styled.div`
  ${p => p.showBorder && `
    border-right: 1px solid ${p.theme.colors.darkThemeSecondary};
    padding-right: 10px;
    `
  }
  display: inline-block;
  margin-right: 5px;
`;

const ConnectivityStatusIcon = styled.i`
  color:
  ${p => p.status === 0 && p.theme.colors.darkThemeSecondary}
  ${p => p.status === 1 && p.theme.colors.finishThemeColor}
  ${p => p.status === 2 && p.theme.colors.errorThemeColor}
  ;
  margin: 0 5px;
`;

const OperationalStatusIcon = styled.i`
  color:
  ${p => p.status === 0 ? p.theme.colors.darkThemeSecondary : p.theme.colors.theme};
  margin: 0 5px;
`;

const Equipment = ({ match }) => {
  const id = match.params.equipmentId;
  const {
    connectionStatus,
    connectionStatusLoading,
    connectionStatusError,
  } = useSelector(state => state.equipment.info);
  const dispatch = useDispatch();
  const intl = useIntl();
  const [checkPermission] = usePermissionCheck();

  const renderConnectionStatus = () => {
    if (id && checkPermission('installedbaseread')
      && isToggleEnabled('smartLinkEquipmentStatus') &&
      connectionStatus?.equipmentId !== id && !connectionStatusLoading &&
      !connectionStatusError
    ) {
      dispatch(fetchConnectionStatus({ id }));
    }
    if (connectionStatus && connectionStatus?.isConnected) {
      const showInfo = connectionStatus?.connectivityStatus?.id !== 0 ||
        connectionStatus?.operationalStatus?.id !== 0;
      const info = intl.formatMessage(
        { id: 'equipment.CONNECTION_STATE' },
        {
          lastUpdated: moment()
            .diff(moment(connectionStatus?.lastExternalApiCommunicationTimestampUtc), 'minute'),
          connectionStatus: connectionStatus?.connectivityStatus?.description,
          dateConnectionStatus:
            moment(connectionStatus?.connectivityStatus?.timestampUtc).format('LLLL'),
          operationalStatus: connectionStatus?.operationalStatus?.description,
          dateOperationalStatus:
            moment(connectionStatus?.operationalStatus?.timestampUtc).format('LLLL'),
        },
      );

      return <Div>
        <WrapperState showBorder={true}>
          <ConnectivityStatusIcon
            status={connectionStatus?.connectivityStatus?.id}
            className={connectionStatus?.connectivityStatus?.icon}
            size='sm'
          />
          <span>{connectionStatus?.connectivityStatus?.description}</span>
        </WrapperState>
        <WrapperState showBorder={showInfo}>
          <OperationalStatusIcon
            status={connectionStatus?.operationalStatus?.id}
            className={connectionStatus?.operationalStatus?.icon}
            size='sm'
          />
          <span>{connectionStatus?.operationalStatus?.description}</span>
        </WrapperState>
        {showInfo &&
          <InfoTooltip
            content={info}
          />}
      </Div>;
    }
    return null;
  };

  const renderMenu = actionButtons =>
    <MainMenu
      id={id}
      type={referenceTypes.equipment}
      actionButtons={actionButtons}
    >
      <SubMenu
        id={id}
        baseType={referenceTypes.equipment}
        match={match}
        hasTickets={true}
        base={`equipment/${id}`}
        extraLinks={
          [
            {
              translationKey: 'nav.subNav.TIMELINE',
              uri: `/equipment/${id}/timeline`,
              absolute: true,
            },
          ]
        }
      />
    </MainMenu>;
  const ticketRoutes = ticketObjectRoutes();
  return (
    <Wrapper>
      <Routes>
        <Route
          path={'dashboard/*'}
          element={<Dashboard
            id={id}
            renderConnectionStatus={renderConnectionStatus}
            renderMenu={renderMenu}
          />}
        />
        <Route
          path={'info/*'}
          element={<Info
            id={id} renderConnectionStatus={renderConnectionStatus} renderMenu={renderMenu}
          />}
        />
        <Route
          path={'timeline/create-order/:type/:ids'}
          element={<>
            <Timeline
              id={id}
              renderConnectionStatus={renderConnectionStatus}
              renderMenu={renderMenu}
            />
            <CreateOrders />
          </>}
        />
        <Route
          path={'timeline/:jumpToDateOrFilter?/create-order/:type/:ids'}
          element={<>
            <Timeline
              id={id}
              renderConnectionStatus={renderConnectionStatus}
              renderMenu={renderMenu}
            />
            <CreateOrders />
          </>}
        />
        <Route
          path={'timeline/:jumpToDateOrFilter?/:filtervalue?/create-order/:type/:ids'}
          element={<>
            <Timeline
              id={id}
              renderConnectionStatus={renderConnectionStatus}
              renderMenu={renderMenu}
            />
            <CreateOrders />
          </>}
        />
        <Route
          path={'timeline/:jumpToDateOrFilter?/:filtervalue?'}
          element={<Timeline
            id={id}
            renderConnectionStatus={renderConnectionStatus}
            renderMenu={renderMenu}
          />
          }
        />
        {ticketRoutes?.map((r, i) => <Route
          path={`${r}/*`}
          key={i}
          element={
            <TicketWorklistContainer
              renderMenu={renderMenu}
              baseType={referenceTypes.equipment}
              renderConnectionStatus={renderConnectionStatus}
            />
          }
        />)}
        <Route
          path='/*'
          element={<Navigate to='dashboard' replace />}
        />
      </Routes>
      <Outlet />
    </Wrapper>
  );
};

Equipment.propTypes = {
  match: PropTypes.object.isRequired,
};

export default withRouter(Equipment);
